import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router";

import { DataTable } from "../../components/DataTable";
import { ConfirmDialog } from "../../components/Dialog";
import {
  Alert,
  Badge,
  Button,
  Card,
  cx,
  LoadingBlock,
  PageHeader,
  SelectField,
  TextField,
} from "../../components/ui";
import { useToast } from "../../components/Toast";
import { api, errorMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useConfig } from "../../lib/config";
import { formatBytes, formatDateTime, todayInZone } from "../../lib/format";
import type { Page } from "../../lib/types";

function useAct() {
  const { notify } = useToast();
  const qc = useQueryClient();
  return async (fn: () => Promise<unknown>, success: string, invalidate: string[]) => {
    try {
      await fn();
      notify(success);
      await Promise.all(invalidate.map((k) => qc.invalidateQueries({ queryKey: [k] })));
      return true;
    } catch (err) {
      notify(errorMessage(err), "error");
      return false;
    }
  };
}

function useTz() {
  return useConfig().data?.business_timezone ?? "UTC";
}

export function AdminLayout() {
  const tabs = [
    ["/admin", "Employees", true],
    ["/admin/users", "Accounts"],
    ["/admin/files", "Files"],
    ["/admin/settings", "Settings"],
    ["/admin/reports", "Reports"],
    ["/admin/notifications", "Email log"],
    ["/admin/audit", "Audit log"],
    ["/admin/jobs", "Jobs"],
    ["/admin/deletions", "File deletions"],
    ["/admin/server", "Server"],
  ] as const;
  return (
    <div>
      <nav
        aria-label="Administration"
        className="mb-6 flex flex-wrap gap-2 border-b border-line pb-2"
      >
        {tabs.map(([to, label, end]) => (
          <NavLink
            key={to}
            to={to}
            end={Boolean(end)}
            className={({ isActive }) =>
              cx(
                "rounded-md px-3 py-2 text-sm font-semibold",
                isActive ? "bg-brand-600 text-white" : "text-brand-700 hover:bg-brand-50",
              )
            }
          >
            {label}
          </NavLink>
        ))}
      </nav>
      <StorageBanner />
      <Outlet />
    </div>
  );
}

// --- Employees -----------------------------------------------------------------------------

interface StaffRow {
  id: string;
  name: string;
  email: string;
  roles: ("employee" | "administrator")[];
  status: "active" | "disabled";
  mfa_enabled: boolean;
  last_login_at: string | null;
}

export function AdminStaffPage() {
  const tz = useTz();
  const { user } = useAuth();
  const act = useAct();
  const q = useQuery({
    queryKey: ["admin-staff"],
    queryFn: () => api.get<StaffRow[]>("/admin/staff"),
  });
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", role: "employee" });
  const [confirm, setConfirm] = useState<{ row: StaffRow; action: "disable" | "mfa" } | null>(null);
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Employees"
        description="Only administrators can add employees and change staff roles. You cannot change your own roles."
      />
      <Card>
        <h2 className="mb-3 text-lg font-bold">Add an employee</h2>
        <form
          className="grid gap-3 sm:grid-cols-4"
          onSubmit={async (e) => {
            e.preventDefault();
            const roles =
              form.role === "administrator" ? ["employee", "administrator"] : ["employee"];
            const ok = await act(
              () =>
                api.post("/admin/staff", {
                  email: form.email,
                  first_name: form.first_name,
                  last_name: form.last_name,
                  roles,
                }),
              "Employee added. They were emailed a link to set their password.",
              ["admin-staff"],
            );
            if (ok) setForm({ email: "", first_name: "", last_name: "", role: "employee" });
          }}
        >
          <TextField
            label="Email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <TextField
            label="First name"
            required
            value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          />
          <TextField
            label="Last name"
            required
            value={form.last_name}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
          />
          <SelectField
            label="Role"
            required
            placeholder="Choose one"
            options={[
              { value: "employee", label: "Employee" },
              { value: "administrator", label: "Administrator" },
            ]}
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
          />
          <Button type="submit" className="self-start">
            Add employee
          </Button>
        </form>
      </Card>
      <DataTable
        caption="Employees"
        loading={q.isLoading}
        rows={q.data}
        rowKey={(r) => r.id}
        columns={[
          {
            key: "name",
            header: "Name",
            render: (r) => (
              <span>
                <Link
                  className="font-semibold text-brand-700 underline"
                  to={`/admin/users/${r.id}`}
                >
                  {r.name}
                </Link>
                <span className="block text-ink-muted">{r.email}</span>
              </span>
            ),
          },
          {
            key: "roles",
            header: "Roles",
            render: (r) =>
              r.roles.map((x) => (
                <Badge key={x} tone={x === "administrator" ? "brand" : "neutral"}>
                  {x}
                </Badge>
              )),
          },
          {
            key: "mfa",
            header: "Two-step",
            render: (r) =>
              r.mfa_enabled ? <Badge tone="ok">On</Badge> : <Badge tone="warn">Not set up</Badge>,
          },
          {
            key: "status",
            header: "Status",
            render: (r) => <Badge tone={r.status === "active" ? "ok" : "danger"}>{r.status}</Badge>,
          },
          {
            key: "login",
            header: "Last sign-in",
            render: (r) => formatDateTime(r.last_login_at, tz) || "Never",
          },
          {
            key: "actions",
            header: "Actions",
            render: (r) =>
              r.id === user?.id ? (
                <span className="text-sm text-ink-muted">You</span>
              ) : (
                <span className="flex flex-wrap gap-1">
                  <Button
                    variant="ghost"
                    onClick={() =>
                      act(
                        () =>
                          api.put(`/admin/users/${r.id}/roles`, {
                            roles: r.roles.includes("administrator")
                              ? ["employee"]
                              : ["employee", "administrator"],
                          }),
                        "Roles updated. They must sign in again.",
                        ["admin-staff"],
                      )
                    }
                  >
                    {r.roles.includes("administrator") ? "Make employee" : "Make administrator"}
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirm({ row: r, action: "mfa" })}>
                    Force two-step reset
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() =>
                      act(
                        () => api.post(`/admin/users/${r.id}/resend-setup`),
                        "Setup link sent.",
                        [],
                      )
                    }
                  >
                    Send setup link
                  </Button>
                  {r.status === "active" ? (
                    <Button
                      variant="ghost"
                      onClick={() => setConfirm({ row: r, action: "disable" })}
                    >
                      Disable
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      onClick={() =>
                        act(
                          () => api.put(`/admin/users/${r.id}/status`, { status: "active" }),
                          "Account enabled.",
                          ["admin-staff"],
                        )
                      }
                    >
                      Enable
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() =>
                      act(
                        () => api.put(`/admin/users/${r.id}/roles`, { roles: [] }),
                        "Staff access removed.",
                        ["admin-staff"],
                      )
                    }
                  >
                    Remove staff access
                  </Button>
                </span>
              ),
          },
        ]}
      />
      <ConfirmDialog
        open={!!confirm}
        title={
          confirm?.action === "disable"
            ? `Disable ${confirm.row.name}?`
            : `Force a two-step verification reset for ${confirm?.row.name}?`
        }
        body={
          confirm?.action === "disable"
            ? "They will be signed out everywhere and cannot sign in until enabled again."
            : "They will be signed out everywhere and must set up two-step verification again (with a new QR code) the next time they sign in."
        }
        confirmLabel={confirm?.action === "disable" ? "Disable" : "Reset"}
        danger
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          const c = confirm!;
          setConfirm(null);
          if (c.action === "disable")
            await act(
              () => api.put(`/admin/users/${c.row.id}/status`, { status: "disabled" }),
              "Account disabled.",
              ["admin-staff"],
            );
          else
            await act(
              () => api.post(`/admin/users/${c.row.id}/reset-mfa`),
              "Two-step reset forced. They will set it up again at next sign-in.",
              ["admin-staff"],
            );
        }}
      />
    </div>
  );
}

// --- Accounts ------------------------------------------------------------------------------

interface SettingRow {
  key: string;
  value: unknown;
  default: unknown;
  value_type: "bool" | "int" | "str" | "list[str]";
  description: string;
}

function SettingEditor({ s }: { s: SettingRow }) {
  const act = useAct();
  const initial = s.value_type === "list[str]" ? (s.value as string[]).join(", ") : String(s.value);
  const [value, setValue] = useState(initial);
  const parse = () => {
    if (s.value_type === "bool") return value === "true";
    if (s.value_type === "int") return Number(value);
    if (s.value_type === "list[str]")
      return value
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
    return value;
  };
  return (
    <form
      className="flex flex-col gap-2 border-b border-line py-4 sm:flex-row sm:items-end"
      onSubmit={(e) => {
        e.preventDefault();
        act(() => api.put(`/admin/settings/${s.key}`, { value: parse() }), "Setting saved.", [
          "admin-settings",
        ]);
      }}
    >
      <div className="flex-1">
        {s.value_type === "bool" ? (
          <SelectField
            label={s.description}
            required
            placeholder="Choose"
            options={[
              { value: "true", label: "Yes" },
              { value: "false", label: "No" },
            ]}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        ) : (
          <TextField
            label={s.description}
            required
            type={s.value_type === "int" ? "number" : "text"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            hint={`Setting: ${s.key}`}
          />
        )}
      </div>
      <Button type="submit" variant="secondary" disabled={value === initial}>
        Save
      </Button>
    </form>
  );
}

export function AdminSettingsPage() {
  const q = useQuery({
    queryKey: ["admin-settings"],
    queryFn: () =>
      api.get<{ settings: SettingRow[]; environment: Record<string, unknown> }>("/admin/settings"),
  });
  if (q.isLoading || !q.data) return <LoadingBlock />;
  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Settings" />
      <Card>
        <h2 className="text-lg font-bold">Business settings</h2>
        {q.data.settings.map((s) => (
          <SettingEditor key={s.key} s={s} />
        ))}
      </Card>
      <Card>
        <h2 className="text-lg font-bold">Server configuration (read-only)</h2>
        <p className="text-sm text-ink-muted">
          These come from the server environment. Passwords and keys are never shown here.
        </p>
        <dl className="mt-3 grid gap-2 sm:grid-cols-2">
          {Object.entries(q.data.environment).map(([k, v]) => (
            <div key={k}>
              <dt className="text-sm font-semibold text-ink-muted">{k}</dt>
              <dd className="break-all">{Array.isArray(v) ? v.join(" – ") : String(v)}</dd>
            </div>
          ))}
        </dl>
      </Card>
    </div>
  );
}

// --- Reports -------------------------------------------------------------------------------

export function AdminReportsPage() {
  const tz = useTz();
  const today = todayInZone(tz);
  const [start, setStart] = useState(() => {
    const d = new Date(`${today}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [end, setEnd] = useState(today);
  const q = useQuery({
    queryKey: ["admin-report", start, end],
    queryFn: () =>
      api.get<{
        total: number;
        by_status: Record<string, number>;
        by_department: Record<string, number>;
        by_day: { date: string; count: number }[];
        open_overdue: number;
      }>(`/admin/reports/orders?start=${start}&end=${end}`),
  });
  const max = Math.max(1, ...(q.data?.by_day.map((d) => d.count) ?? [1]));
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Order report"
        actions={
          <a
            className="inline-flex min-h-11 items-center rounded-md border border-brand-600 px-4 font-semibold text-brand-700"
            href={`/api/v1/admin/reports/orders.csv?start=${start}&end=${end}`}
          >
            Download CSV
          </a>
        }
      />
      <div className="grid max-w-md grid-cols-2 gap-3">
        <TextField
          label="From"
          type="date"
          value={start}
          onChange={(e) => setStart(e.target.value)}
        />
        <TextField label="To" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
      </div>
      {q.data && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Card>
              <p className="text-sm text-ink-muted">Orders in range</p>
              <p className="text-3xl font-bold">{q.data.total}</p>
            </Card>
            <Card>
              <p className="text-sm text-ink-muted">Open and past due (now)</p>
              <p className="text-3xl font-bold">{q.data.open_overdue}</p>
            </Card>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {[
              ["By status", q.data.by_status],
              ["By department", q.data.by_department],
            ].map(([title, data]) => (
              <Card key={title as string}>
                <h2 className="mb-2 font-bold">{title as string}</h2>
                <table className="w-full text-sm">
                  <caption className="sr-only">{title as string}</caption>
                  <tbody>
                    {Object.entries(data as Record<string, number>).map(([k, v]) => (
                      <tr key={k} className="border-t border-line">
                        <th scope="row" className="py-1 text-left font-normal">
                          {k}
                        </th>
                        <td className="py-1 text-right font-semibold">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            ))}
          </div>
          <Card>
            <h2 className="mb-2 font-bold">Orders per day</h2>
            <ul className="flex flex-col gap-1 text-sm">
              {q.data.by_day.map((d) => (
                <li key={d.date} className="flex items-center gap-2">
                  <span className="w-24 shrink-0">{d.date}</span>
                  <span
                    className="h-3 rounded bg-brand-500"
                    style={{ width: `${(d.count / max) * 70}%` }}
                    aria-hidden="true"
                  />
                  <span>{d.count}</span>
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}
    </div>
  );
}

// --- Email log -----------------------------------------------------------------------------

interface NotificationRow {
  id: string;
  template_key: string;
  recipient: string;
  subject: string;
  status: "pending" | "sending" | "sent" | "failed" | "cancelled";
  attempt_count: number;
  created_at: string;
  sent_at: string | null;
  last_error: string | null;
}

export function AdminNotificationsPage() {
  const tz = useTz();
  const act = useAct();
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const data = useQuery({
    queryKey: ["admin-notifications", status, q, page],
    queryFn: () =>
      api.get<Page<NotificationRow>>(
        `/admin/notifications?${new URLSearchParams({ ...(status ? { status } : {}), q, page: String(page) })}`,
      ),
  });
  const tone = {
    sent: "ok",
    failed: "danger",
    pending: "warn",
    sending: "warn",
    cancelled: "neutral",
  } as const;
  return (
    <div>
      <PageHeader
        title="Email log"
        description="Every email the portal sends. Failed emails can be retried; one-time links cannot be resent (the person must request a new one)."
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <TextField
          label="Search"
          placeholder="Recipient or subject"
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
        />
        <SelectField
          label="Status"
          placeholder="All"
          options={["pending", "sent", "failed"].map((v) => ({ value: v, label: v }))}
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
        />
      </div>
      <DataTable
        caption="Email log"
        loading={data.isLoading}
        rows={data.data?.items}
        rowKey={(r) => r.id}
        page={page}
        pageSize={50}
        total={data.data?.total}
        onPage={setPage}
        columns={[
          { key: "when", header: "Queued", render: (r) => formatDateTime(r.created_at, tz) },
          { key: "to", header: "To", render: (r) => r.recipient },
          {
            key: "subject",
            header: "Subject",
            render: (r) => (
              <span>
                {r.subject}
                <span className="block text-xs text-ink-muted">{r.template_key}</span>
              </span>
            ),
          },
          {
            key: "status",
            header: "Status",
            render: (r) => (
              <span>
                <Badge tone={tone[r.status]}>{r.status}</Badge>
                {r.last_error && <span className="block text-xs text-danger">{r.last_error}</span>}
              </span>
            ),
          },
          { key: "tries", header: "Attempts", render: (r) => r.attempt_count },
          {
            key: "act",
            header: "",
            render: (r) =>
              r.status === "sent" || r.status === "failed" ? (
                <Button
                  variant="ghost"
                  onClick={() =>
                    act(() => api.post(`/admin/notifications/${r.id}/resend`), "Email queued.", [
                      "admin-notifications",
                    ])
                  }
                >
                  {r.status === "failed" ? "Retry" : "Resend"}
                </Button>
              ) : null,
          },
        ]}
      />
    </div>
  );
}

// --- Audit log -----------------------------------------------------------------------------

interface AuditRow {
  id: string;
  created_at: string;
  actor: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  summary: Record<string, unknown>;
  ip_address: string | null;
}

export function AdminAuditPage() {
  const tz = useTz();
  const [filters, setFilters] = useState({ action: "", entity_id: "", actor: "" });
  const [page, setPage] = useState(1);
  const data = useQuery({
    queryKey: ["admin-audit", filters, page],
    queryFn: () => {
      const p = new URLSearchParams({ page: String(page) });
      Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
      return api.get<Page<AuditRow>>(`/admin/audit?${p}`);
    },
  });
  return (
    <div>
      <PageHeader
        title="Audit log"
        description="A permanent record of important actions. Entries cannot be changed or deleted."
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <TextField
          label="Action starts with"
          placeholder="order."
          value={filters.action}
          onChange={(e) => {
            setPage(1);
            setFilters({ ...filters, action: e.target.value });
          }}
        />
        <TextField
          label="Item ID"
          value={filters.entity_id}
          onChange={(e) => {
            setPage(1);
            setFilters({ ...filters, entity_id: e.target.value });
          }}
        />
        <TextField
          label="Person (email)"
          value={filters.actor}
          onChange={(e) => {
            setPage(1);
            setFilters({ ...filters, actor: e.target.value });
          }}
        />
      </div>
      <DataTable
        caption="Audit log"
        loading={data.isLoading}
        rows={data.data?.items}
        rowKey={(r) => r.id}
        page={page}
        pageSize={50}
        total={data.data?.total}
        onPage={setPage}
        columns={[
          { key: "when", header: "When", render: (r) => formatDateTime(r.created_at, tz) },
          { key: "who", header: "Who", render: (r) => r.actor ?? "System" },
          {
            key: "action",
            header: "Action",
            render: (r) => <code className="text-xs">{r.action}</code>,
          },
          {
            key: "item",
            header: "Item",
            render: (r) => (
              <span className="text-xs">
                {r.entity_type} {r.entity_id?.slice(0, 8)}
              </span>
            ),
          },
          {
            key: "details",
            header: "Details",
            render: (r) => (
              <code className="block max-w-xs truncate text-xs" title={JSON.stringify(r.summary)}>
                {JSON.stringify(r.summary)}
              </code>
            ),
          },
        ]}
      />
    </div>
  );
}

// --- Jobs ----------------------------------------------------------------------------------

interface JobRow {
  name: string;
  description: string;
  interval_seconds: number;
  running: boolean;
  last_run: {
    state: string;
    started_at: string;
    finished_at: string | null;
    detail: Record<string, unknown>;
    error: string | null;
  } | null;
}

export function AdminJobsPage() {
  const tz = useTz();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [result, setResult] = useState<{ name: string; detail: unknown } | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["admin-jobs"],
    queryFn: () => api.get<{ jobs: JobRow[] }>("/admin/jobs"),
    refetchInterval: 15_000,
  });
  const run = async (name: string, dryRun: boolean) => {
    try {
      const r = await api.post<{ detail?: unknown; message?: string; state?: string }>(
        `/admin/jobs/${name}/run`,
        { dry_run: dryRun },
      );
      if (r.message) notify(r.message, "info");
      else setResult({ name: `${name}${dryRun ? " (preview)" : ""}`, detail: r.detail });
      await qc.invalidateQueries({ queryKey: ["admin-jobs"] });
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Background jobs"
        description="The worker runs these automatically. Preview shows what a job would do without changing anything."
      />
      {result && (
        <Alert tone="info" title={`Result: ${result.name}`}>
          <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-xs">
            {JSON.stringify(result.detail, null, 2)}
          </pre>
        </Alert>
      )}
      <DataTable
        caption="Background jobs"
        loading={q.isLoading}
        rows={q.data?.jobs}
        rowKey={(j) => j.name}
        columns={[
          {
            key: "name",
            header: "Job",
            render: (j) => (
              <span>
                <code>{j.name}</code>
                <span className="block text-sm text-ink-muted">{j.description}</span>
              </span>
            ),
          },
          {
            key: "every",
            header: "Every",
            render: (j) =>
              j.interval_seconds >= 3600
                ? `${j.interval_seconds / 3600} h`
                : `${Math.round(j.interval_seconds / 60) || j.interval_seconds + " s"}${j.interval_seconds >= 60 ? " min" : ""}`,
          },
          {
            key: "last",
            header: "Last run",
            render: (j) =>
              j.last_run ? (
                <span>
                  <Badge
                    tone={
                      j.last_run.state === "success"
                        ? "ok"
                        : j.last_run.state === "failed"
                          ? "danger"
                          : "warn"
                    }
                  >
                    {j.last_run.state}
                  </Badge>{" "}
                  {formatDateTime(j.last_run.started_at, tz)}
                  {j.last_run.error && (
                    <span className="block text-xs text-danger">{j.last_run.error}</span>
                  )}
                </span>
              ) : (
                "Not yet"
              ),
          },
          {
            key: "act",
            header: "",
            render: (j) => (
              <span className="flex gap-1">
                <Button variant="ghost" onClick={() => run(j.name, true)}>
                  Preview
                </Button>
                <Button variant="secondary" disabled={j.running} onClick={() => setConfirm(j.name)}>
                  Run now
                </Button>
              </span>
            ),
          },
        ]}
      />
      <ConfirmDialog
        open={!!confirm}
        title={`Run ${confirm} now?`}
        body="This does the job for real (for example, sending emails or deleting expired files). Use Preview first if you are unsure."
        confirmLabel="Run now"
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          const name = confirm!;
          setConfirm(null);
          await run(name, false);
        }}
      />
    </div>
  );
}

// --- File deletions ------------------------------------------------------------------------

export function AdminDeletionsPage() {
  const tz = useTz();
  const q = useQuery({
    queryKey: ["admin-deletions"],
    queryFn: () =>
      api.get<{
        events: {
          at: string;
          action: string;
          file_id: string;
          summary: { name?: string; size?: number };
        }[];
        currently_failing: {
          kind: string;
          id: string;
          name: string;
          attempts: number;
          error: string | null;
        }[];
        order_files_expiring_within_7_days: number;
      }>("/admin/files/deletions"),
  });
  if (q.isLoading || !q.data) return <LoadingBlock />;
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="File expiry and deletion"
        description="Print files are deleted 90 days after completion; Files FROM Bill's 45 days after release."
      />
      <p>
        {q.data.order_files_expiring_within_7_days} print file(s) will be deleted in the next 7
        days.
      </p>
      {q.data.currently_failing.length > 0 && (
        <Alert tone="error" title="Deletions that are failing (retried automatically)">
          <ul>
            {q.data.currently_failing.map((f) => (
              <li key={f.id}>
                {f.kind}: {f.name} · {f.attempts} attempts · {f.error}
              </li>
            ))}
          </ul>
        </Alert>
      )}
      <DataTable
        caption="Deletion history"
        rows={q.data.events}
        rowKey={(e) => `${e.file_id}-${e.at}`}
        emptyTitle="No files have been deleted yet"
        columns={[
          { key: "at", header: "When", render: (e) => formatDateTime(e.at, tz) },
          {
            key: "what",
            header: "Event",
            render: (e) => <code className="text-xs">{e.action}</code>,
          },
          { key: "name", header: "File", render: (e) => e.summary.name ?? e.file_id },
        ]}
      />
    </div>
  );
}

// --- Server capacity -----------------------------------------------------------------------

interface StorageStatus {
  level: "ok" | "warning" | "critical";
  percent_used: number;
  warning_percent: number;
  critical_percent: number;
  disk_total_bytes: number;
  disk_used_bytes: number;
  disk_free_bytes: number;
  order_files_bytes: number;
  delivery_files_bytes: number;
  uploads_in_progress_bytes: number;
  database_bytes: number | null;
}

function useStorage() {
  return useQuery({
    queryKey: ["admin-storage"],
    queryFn: () => api.get<StorageStatus>("/admin/system/storage"),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
  });
}

/** Shown to administrators across staff/admin pages when the disk is filling up. */
export function StorageBanner() {
  const { data } = useStorage();
  if (!data || data.level === "ok") return null;
  return (
    <div className="mb-4">
      <Alert
        tone={data.level === "critical" ? "error" : "warning"}
        title={`Server storage is ${data.percent_used}% full`}
      >
        {formatBytes(data.disk_free_bytes)} free.{" "}
        {data.level === "critical"
          ? "Uploads may soon fail. Add storage or remove old files now."
          : "Plan to add storage soon."}{" "}
        <Link className="underline" to="/admin/server">
          Server details
        </Link>
      </Alert>
    </div>
  );
}

export function AdminServerPage() {
  const q = useStorage();
  if (q.isLoading) return <LoadingBlock />;
  if (!q.data) return <Alert tone="error">{errorMessage(q.error)}</Alert>;
  const s = q.data;
  const barTone =
    s.level === "critical" ? "bg-danger" : s.level === "warning" ? "bg-warn" : "bg-ok";
  const rows: [string, number | null][] = [
    ["Customer print files", s.order_files_bytes],
    ["Files FROM Bill's", s.delivery_files_bytes],
    ["Uploads in progress", s.uploads_in_progress_bytes],
    ["Database", s.database_bytes],
  ];
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Server"
        description={`Warnings start at ${s.warning_percent}% full and become urgent at ${s.critical_percent}%. Change these under Settings. Administrators are also emailed once a day while storage is over a limit.`}
      />
      <Card>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-bold">Disk space</h2>
          <Badge tone={s.level === "ok" ? "ok" : s.level === "warning" ? "warn" : "danger"}>
            {s.level === "ok" ? "Healthy" : s.level === "warning" ? "Getting full" : "Nearly full"}
          </Badge>
        </div>
        <div
          className="mt-3 h-4 w-full overflow-hidden rounded-full bg-canvas"
          role="meter"
          aria-label="Disk space used"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={s.percent_used}
        >
          <div
            className={`h-full ${barTone}`}
            style={{ width: `${Math.min(100, s.percent_used)}%` }}
          />
        </div>
        <p className="mt-2">
          <strong>{s.percent_used}%</strong> used · {formatBytes(s.disk_used_bytes)} of{" "}
          {formatBytes(s.disk_total_bytes)} · <strong>{formatBytes(s.disk_free_bytes)} free</strong>
        </p>
      </Card>
      <Card>
        <h2 className="mb-2 text-lg font-bold">What the portal is storing</h2>
        <table className="w-full text-sm">
          <caption className="sr-only">Portal storage use</caption>
          <tbody>
            {rows.map(([label, bytes]) => (
              <tr key={label} className="border-t border-line">
                <th scope="row" className="py-2 text-left font-normal">
                  {label}
                </th>
                <td className="py-2 text-right font-semibold">
                  {bytes === null ? "—" : formatBytes(bytes)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 text-sm text-ink-muted">
          The rest of the disk is used by the operating system, the virus scanner's signature
          database, backups and logs. Print files are removed 90 days after completion and Files
          FROM Bill's 45 days after release.
        </p>
      </Card>
    </div>
  );
}
